import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import ProductSearchSelect from '@/components/ProductSearchSelect';
import config from '@/config';
import { Label } from '@/design_system/Label/Label';
import PhotoCard from '@/design_system/PhotoCard';
import {
  RadioCard,
  RadioCardGroup,
  RadioCardYesNo,
} from '@/design_system/Radio/RadioCard/RadioCard';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { useUpdateArticle } from '@/models/article';
import { ProductFromGetMany } from '@/models/product';
import {
  getRootProductAttributes,
  getSelectableProductAttributeValues,
  getSelectedProductAttributeValueIdsWithAutoSelectedSingleChildren,
  ProductAttribute,
} from '@/models/productAttribute';
import { ArticleWithRelations } from '@/models/request';
import { useCurrentOrganization } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './ProductInfo.css';

const { block, element } = createBEMClasses('client-product-info');

const Reference = ({ article, disabled }: { article: ArticleWithRelations; disabled: boolean }) => {
  const { t } = useLingui();
  const { request, productAttributesData } = useBrandRequestContext();
  const productAttributes =
    productAttributesData.find(({ brandId }) => brandId === article?.brandId)?.productAttributes ??
    [];
  const rootProductAttributes = getRootProductAttributes({ productAttributes });

  const [hasReference, setHasReference] = useState<'yes' | 'no' | undefined>(
    article.product ? 'yes' : article.productAttributeValues.length ? 'no' : undefined
  );

  const { mutate: updateArticle } = useUpdateArticle({
    articleId: article.id,
    requestId: request.id,
  });

  const updateProduct = (product: ProductFromGetMany | null) => {
    if (product === null && !!article.product) {
      updateArticle({
        data: {
          productId: null,
        },
        optimisticData: {
          product: null,
          productAttributeValues: [],
          data: {},
        },
      });
    } else if (product && article.product?.externalId !== product.externalId) {
      updateArticle({
        data: {
          productId: product.id,
        },
        optimisticData: {
          product: {
            ...product,
            productAttributeValues: [],
            brand: null,
          },
          data: {},
        },
      });
    }
  };

  return (
    <Stack gap="1.5rem" className={block()}>
      <Label
        label={t({
          id: 'client.new.article.form.has-reference.label',
          message: 'Do you have the item reference?',
        })}
        size="large"
      >
        <RadioCardYesNo
          name="has-reference"
          theme="brand"
          value={hasReference}
          onChange={(value) => {
            setHasReference(value ?? undefined);
            updateProduct(null);
          }}
          disabled={disabled}
          scrollToOnMount
        />
      </Label>
      {hasReference === 'yes' && (
        <ProductSearchSelect
          article={article}
          value={article.product}
          onChange={(product) => updateProduct(product ?? null)}
          variant="brand"
          showLabel={false}
          placeholder={t({
            id: 'client.new.article.form.reference.placeholder',
            message: 'Enter your item reference number or the name of the item',
          })}
          isDisabled={disabled}
          focusOnRender
          noOptionsMessage={
            <Stack padding="0.5rem" style={{ background: 'var(--color-neutral-0)' }}>
              <Paragraph>
                <Trans id="client.new.article.form.reference.no-options">
                  Your reference could not be found. Please try another reference or proceed without
                  one by clicking &quot;No&quot;
                </Trans>
              </Paragraph>
            </Stack>
          }
        />
      )}
      {hasReference !== 'no' && <ReferenceHelp />}
      {hasReference === 'no' && (
        <Stack gap="3rem" className={block()}>
          {rootProductAttributes.map((productAttribute) => (
            <ProductAttributeValueRadio
              key={productAttribute.id}
              productAttribute={productAttribute}
              article={article}
              disabled={disabled}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const ReferenceHelp = () => {
  const [organization] = useCurrentOrganization();
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { isMobile } = useViewPort();

  const referencePhotoAlt = t({
    id: 'client-new.article.form.has-reference.photo-alt',
    message: `Photo of an item's reference`,
  });

  return (
    <Stack
      row
      gap="1rem"
      flexWrap="nowrap"
      alignItems="center"
      className={element('reference-help')}
    >
      <PhotoCard
        url={
          organization?.config.appStyle.diagnosticForm?.referenceHelper?.image ??
          `${config.appUrl}/brands/reference/${
            import.meta.env.VITE_DEFAULT_ORGANIZATION_LOGO ? 'prolong-acme' : organization?.slug
          }.webp`
        }
        name={referencePhotoAlt}
        alt={referencePhotoAlt}
        size="xlarge"
        hideMoreIcon
      />
      <Stack gap="0.5rem">
        <Paragraph size="50" weight="medium" className="is-hidden-mobile">
          <Trans id="client-new.article.form.has-reference.help-title">Where can I find it?</Trans>
        </Paragraph>
        {organization?.config.appStyle.diagnosticForm?.referenceHelper?.text && (
          <Paragraph size={isMobile ? '100' : '50'}>
            {_db(organization.config.appStyle.diagnosticForm.referenceHelper.text)}
          </Paragraph>
        )}
      </Stack>
    </Stack>
  );
};

const ProductAttributeValueRadio = ({
  productAttribute,
  article,
  disabled,
}: {
  productAttribute: ProductAttribute;
  article: ArticleWithRelations;
  disabled: boolean;
}) => {
  const { isMobile } = useViewPort();
  const { request, productAttributesData } = useBrandRequestContext();
  const productAttributes =
    productAttributesData.find(({ brandId }) => brandId === article?.brandId)?.productAttributes ??
    [];
  const { _db, _compare } = useDatabaseTranslation();
  const selectedValueIds = article.productAttributeValues.map((value) => value.id);
  const isParentSelected =
    !productAttribute.parentProductAttributeValueId ||
    selectedValueIds.includes(productAttribute.parentProductAttributeValueId);

  const { mutate: updateArticle } = useUpdateArticle({
    articleId: article.id,
    requestId: request.id,
  });

  if (!productAttribute.enabled || !isParentSelected) {
    return null;
  }

  const selectedValue =
    article.productAttributeValues.find((value) =>
      productAttribute.productAttributeValues.some((option) => option.id === value.id)
    ) ?? null;
  const options = getSelectableProductAttributeValues({
    productAttributeValues: productAttribute.productAttributeValues,
    selectedValueIds,
  }).sort((value1, value2) => _compare(value1.name, value2.name));

  const childAttributes = productAttributes.filter(
    (attribute) => attribute.parentProductAttributeValueId === selectedValue?.id
  );

  if (options.length <= 1) {
    return null;
  }

  return (
    <Stack gap="1.5rem">
      <Label label={_db(productAttribute.name)} size="large">
        <RadioCardGroup
          name={`product-attribute-${productAttribute.id}`}
          theme="brand"
          direction={isMobile ? 'column' : 'row'}
          style={
            isMobile ? undefined : { gridTemplateColumns: 'repeat(auto-fit, minmax(12.5rem, 1fr))' }
          }
          value={selectedValue?.id}
          allowDeselect={!productAttribute.required}
          onChange={(optionId) => {
            const selectedIdsWithoutCurrentAttribute = selectedValueIds.filter(
              (valueId) =>
                !productAttribute.productAttributeValues.some(
                  (productAttributeValue) => productAttributeValue.id === valueId
                )
            );
            const nextSelectedValueIds = optionId
              ? [...selectedIdsWithoutCurrentAttribute, optionId]
              : selectedIdsWithoutCurrentAttribute;
            const filteredSelectedValueIds =
              getSelectedProductAttributeValueIdsWithAutoSelectedSingleChildren({
                productAttributes,
                selectedValueIds: nextSelectedValueIds,
              });

            updateArticle({
              data: {
                productAttributeValues: productAttributes
                  .flatMap((attribute) => attribute.productAttributeValues)
                  .filter((value) => filteredSelectedValueIds.includes(value.id)),
              },
            });
          }}
          disabled={disabled}
          scrollToOnMount
        >
          {options.map((option) => (
            <RadioCard
              key={option.id}
              value={option.id}
              ariaLabel={_db(option.name)}
              picture={
                option.illustration?.url ? (
                  <img src={option.illustration.url} alt="" />
                ) : option.illustration?.backgroundUrl ? (
                  <div
                    style={{
                      backgroundImage: `url("${option.illustration.backgroundUrl}")`,
                      backgroundSize: 'cover',
                      height: '100%',
                      width: '100%',
                    }}
                  />
                ) : null
              }
              backgroundColor={option.illustration?.backgroundColor}
            >
              {_db(option.name)}
            </RadioCard>
          ))}
        </RadioCardGroup>
      </Label>
      {childAttributes.map((childAttribute) => (
        <ProductAttributeValueRadio
          key={childAttribute.id}
          productAttribute={childAttribute}
          article={article}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
};

export default Reference;
